/**
 * Runtime Configuration Loader
 * Loads environment-specific configuration at runtime from /config.json
 */

export interface RuntimeConfig {
  apiUrl: string;
  mediaUrl: string;
  environment: string;
  version?: string;
  ga4MeasurementId?: string;
  features?: {
    analytics?: boolean;
    debug?: boolean;
  };
  auth0?: {
    domain: string;
    clientId: string;
    audience: string;
  };
}

let configCache: RuntimeConfig | null = null;
let configPromise: Promise<RuntimeConfig> | null = null;

/**
 * Loads configuration from /config.json at runtime
 * Uses caching to avoid multiple requests
 * @throws Error if config cannot be loaded or required fields are missing
 */
export async function loadRuntimeConfig(): Promise<RuntimeConfig> {
  // Return cached config if available
  if (configCache) {
    return configCache;
  }

  // Return existing promise if already loading
  if (configPromise) {
    return configPromise;
  }

  // Create new promise to load config
  configPromise = (async () => {
    console.log('🔧 Loading runtime configuration...');
    
    const response = await fetch('/config.json', {
      cache: 'no-cache', // Always get fresh config
      headers: {
        'Cache-Control': 'no-cache',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to load config: ${response.status} ${response.statusText}`);
    }

    const config: RuntimeConfig = await response.json();
    
    // Validate required fields
    if (!config.apiUrl) {
      throw new Error('Runtime config missing required field: apiUrl');
    }
    if (!config.mediaUrl) {
      throw new Error('Runtime config missing required field: mediaUrl');
    }

    // Cache the config
    configCache = config;
    
    console.log('✅ Runtime configuration loaded:', {
      environment: config.environment,
      apiUrl: config.apiUrl,
      mediaUrl: config.mediaUrl,
      version: config.version,
    });

    return config;
  })();

  return configPromise;
}

/**
 * Gets the cached configuration (synchronous)
 * Returns null if config hasn't been loaded yet
 */
export function getCachedConfig(): RuntimeConfig | null {
  return configCache;
}

/**
 * Clears the configuration cache
 * Useful for testing or forcing a reload
 */
export function clearConfigCache(): void {
  configCache = null;
  configPromise = null;
}
